import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import './fromConfiguration.scss';

const MissingTypeConfig = (type) => () => <Typography>{`No component registered for ${type}`}</Typography>;

const fromConfiguration = ({ type, data: dataConfigs, configuration, ...compDescRest }) => {
  let Component;
  if (!dataConfigs) {
    class ConfiguredComponent extends React.PureComponent {
      static propTypes = {
        viewService: PropTypes.shape({
          getComponent: PropTypes.func,
        }).isRequired,
      }

      constructor(props) {
        super(props);
        this.Component = props.viewService.getComponent(type);
        if (!this.Component) {
          console.log(`No registered component for type ${type}`);
          this.Component = MissingTypeConfig(type);
        }
      }

      render() {
        return <this.Component {...this.props} {...compDescRest} configuration={configuration} />;
      }
    }
    Component = ConfiguredComponent;
  } else {
    // eslint-disable-next-line react/no-multi-comp
    class DataContainer extends React.PureComponent {
      static propTypes = {
        viewService: PropTypes.shape({
          getComponent: PropTypes.func,
        }).isRequired,
        dataService: PropTypes.shape({
          getEmptyValue: PropTypes.func.isRequired,
          request: PropTypes.func.isRequired,
          makeDataHandler: PropTypes.func.isRequired,
        }).isRequired,
        configuration: PropTypes.shape({}),
      }

      static defaultProps = {
        configuration: undefined,
      }

      constructor(props) {
        super(props);
        this.state = {
          loading: true,
          ...this.getEmptyDataState(),
        };
        this.dataService = props.dataService;
        this.dataObserver = { onUpdateAll: this.onDataChanged.bind(this) };
        this.Component = props.viewService.getComponent(type);
        this.updateDataHandlers();
      }

      componentDidMount() {
        this.dataService.register(null, this.dataObserver);
        this.loadData();
      }

      componentWillUnmount() {
        this.dataService.unregister(null, this.dataObserver);
        this.unmounted = true;
      }

      onDataChanged(path, newData) {
        if (!this.loading) {
          // If this view component displays data from the specified path, update this data
          const matchPath = dataConfigs.find((dataCfg) => {
            return dataCfg.path === path;
          });
          if (matchPath) {
            this.setData({ [path]: newData });
          }
        }
      }

      getEmptyDataState() {
        const state = {};
        const { dataService } = this.props;
        dataConfigs.forEach((dataConfig) => {
          const { path } = dataConfig;
          // Ensures default value
          if (path && !state[path]) {
            state[path] = dataService.getEmptyValue(path);
          }
        });
        return state;
      }

      getData(dataCfg) {
        const data = this.state[dataCfg.path];
        if (!data) return data;
        return dataCfg.property ? data[dataCfg.property] : data;
      }

      setData(dataState) {
        // Check for no data
        // If at least one data set is null, we consider we don't have data.
        // This deals with the case a view is associated with two data sets,
        // on input data data set non nul and one solution data that is null if the scenario
        // was not optimize. In this case: solution data === null means the view has no data.
        if (!this.unmounted) {
          const noData = Boolean(dataConfigs.find((dataCfg) => {
            const obj = dataCfg.path in dataState ? dataState[dataCfg.path] : this.state[dataCfg.path];
            return (obj === null) || (obj === undefined);
          }));
          this.setState({ ...dataState, hasData: !noData });
        }
      }

      updateDataHandlers() {
        this.dataChangeHandlers = [];
        const { dataService } = this.props;
        dataConfigs.forEach((dataConfig) => {
          const { path } = dataConfig;
          this.dataChangeHandlers.push(dataService.makeDataHandler(
            path,
            dataConfig.property,
            this.props,
            () => {},
          ));
        });
      }

      loadData() {
        this.loading = true;
        const { dataService } = this.props;

        const promisesByPath = {};
        const promises = [];
        const dataState = {};
        dataConfigs.forEach((dataCfg) => {
          const { path, parameters } = dataCfg;
          if (!promisesByPath[path]) {
            promisesByPath[path] = true;
            promises.push(dataService
                .request(path, 'get', null, parameters || {})
                .then((data) => {
                  dataState[path] = data;
                }));
          }
        });
        // Uncomment to test loading panel
        // promisesByPath.wait = new Promise((resolve) => {
        //   setTimeout(resolve, 30000);
        // });
        dataState.loading = false;
        axios.all(promises).then(() => {
          if (!this.unmounted) {
            this.setData(dataState);
          }
        }).catch(() => {
          this.setData({ ...this.getEmptyDataState(), loading: false });
        });
      }

      render() {
        const { loading } = this.state;
        if (loading) {
          return (
            <Paper
              className="tuc-view-component grb-message-component"
            >
              <div className="grb-message-content">
                <Typography variant="h4">Loading</Typography>
                <LinearProgress style={{ width: '100%' }} />
              </div>
            </Paper>
          );
        }
        const models = dataConfigs.map((dataCfg, dataIndex) => {
            return {
              ...dataCfg,
              data: this.getData(dataCfg),
              ...this.dataChangeHandlers[dataIndex],
            };
          });
        return <this.Component {...this.props} {...compDescRest} models={models} data={dataConfigs} configuration={configuration} />;
      }
    }
    Component = DataContainer;
  }
  return Component;
};

export default fromConfiguration;
